import React, { useEffect, useState } from 'react';
import { SuccessMessage } from './SuccessMessage';

import { createStyles } from 'utils/createStyle';
import { Btn } from 'components/btn/Btn';
import { EASE_OUT_QUART, fadeIn, fadeOut } from 'styles/easing';
import {
    addUserToOrgThunk,
    resetOrgJoin,
    selectOrgJoinAsyncState,
} from 'common/features/store/duck/home/duck';
import { useDispatch, useSelector } from 'react-redux';
import { ActivityIndicator } from 'components/activity-indicator/ActivityIndicator';
import { Modal, ModalSize } from 'components/modal/Modal';
import { TextField } from 'components/fields/TextField';

const styles = createStyles({
    popover: [
        'border border-solid border-grey-5 bg-white px-4 py-3 duration-200',
        {
            '&[data-state="open"]': { animation: `${fadeIn} 200ms ${EASE_OUT_QUART}` },
            '&[data-state="closed"]': { animation: `${fadeOut} 200ms ${EASE_OUT_QUART}` },
        },
    ],
    container: [
        'flex flex-col items-top w-full px-2 md:px-10',
        {
            maxWidth: '650px',
        },
    ],
    title: ['font-bold pb-2 md:px-4 px-0 text-xl md:text-3xl', { lineHeight: '35px' }],
    subTitle: ['text-center text-grey-1 text-md', { lineHeight: '24px' }],
    buttonCancel: ['text-brand underline mt-2 font-semibold', { height: 40 }],
});

type JoinAnotherOrganizationContentProps = {
    onFinish: () => void;
};

const JoinAnotherOrganizationContent = ({ onFinish }: JoinAnotherOrganizationContentProps) => {
    const [enrollmentCode, setEnrollmentCode] = useState('');
    const dispatch = useDispatch<any>();
    const { loading, error, lastUpdate, args } = useSelector(selectOrgJoinAsyncState);
    const newOrgName = args && args[0];
    const [displaySuccess, setDisplaySuccess] = useState<boolean>(false);

    const clickHandler = () => {
        dispatch(addUserToOrgThunk(enrollmentCode));
    };

    const onCancel = () => {
        onFinish();
        dispatch(resetOrgJoin(null));
    };

    /**
     * Effect: When loading is complete and we have a lastUpdate timestamp
     * 1) Display success message.
     * 2) Set a timer for 2 seconds that will hide the success message, reset the orgJoin state
     *    and call the onFinish callback which refreshes the parent component and fetches a current
     *    list of orgs containing the org that was just joined.
     */
    useEffect(() => {
        if (!loading && lastUpdate > 0 && !error) {
            setDisplaySuccess(true);
        }
    }, [loading, lastUpdate, error]);

    /**
     * Effect: clears enrollment code input on error
     */
    useEffect(() => {
        if (error) {
            setEnrollmentCode('');
        }
    }, [error]);

    if (displaySuccess) {
        return <SuccessMessage orgName={newOrgName} onClose={() => onCancel()} />;
    }

    return (
        <div className={styles.container}>
            <div className="flex flex-col items-center mb-10 ">
                <h1 className={styles.title}>Join another organization</h1>
                <h4 className={styles.subTitle}>
                    Enter enrollment code to join another organization
                </h4>
            </div>
            <div className="px-0">
                <TextField
                    error={error as string}
                    label="Enrollment Code"
                    className="w-full border-grey-3"
                    errorClassName="text-xs pt-2"
                    value={enrollmentCode}
                    onChange={(e) => {
                        setEnrollmentCode(e.target.value);
                    }}
                />
                {!error && (
                    <span className="text-grey-1 font-normal text-xs pt-1">
                        Organization coordinator has the enrollment code.
                    </span>
                )}
                <div className="flex flex-col items-center mt-12">
                    {loading && (
                        <div className="flex justify-end">
                            <ActivityIndicator />
                        </div>
                    )}
                    {!loading && (
                        <>
                            <Btn
                                disabled={!enrollmentCode}
                                type="button"
                                className="w-full md:w-32 max-w-xs md:py-1.5"
                                onClick={clickHandler}
                            >
                                Join
                            </Btn>
                            <button
                                type="button"
                                className={styles.buttonCancel}
                                onClick={onCancel}
                            >
                                Cancel
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

interface JoinAnotherOrganizationProps {
    disabled?: boolean;
    className?: string;
    children: React.ReactNode;
    orgJoinCompleteCallback: Function;
}

export const JoinAnotherOrganization = ({
    disabled = false,
    className = 'hidden md:block',
    children,
    orgJoinCompleteCallback,
}: React.PropsWithChildren<JoinAnotherOrganizationProps>) => {
    const [open, setOpen] = useState<boolean>(false);
    const dispatch = useDispatch<any>();

    const onClick = () => {
        if (!disabled) {
            setOpen(true);
        }
    };

    const orgJoinComplete = (setOpenState: Function) => {
        setOpenState(false);
        orgJoinCompleteCallback();
    };

    /**
     * Effect: when the flyout is closed, reset the org join state
     * which resets the error state of the form.
     */
    useEffect(() => {
        if (!open) {
            dispatch(resetOrgJoin(null));
        }
    }, [dispatch, open]);

    return (
        <div className="inline-block">
            <div className={className}>
                <div className="block">
                    <span className="cursor-pointer" onClick={onClick}>
                        {children}
                    </span>
                </div>
                <Modal
                    title=""
                    isOpen={open}
                    onClose={() => setOpen(false)}
                    showCloseButton
                    size={[ModalSize.EXTRA_SMALL, ModalSize.MEDIUM]}
                >
                    <JoinAnotherOrganizationContent onFinish={() => orgJoinComplete(setOpen)} />
                </Modal>
            </div>
        </div>
    );
};
